import type { FormEvent } from 'react';
import React from 'react';

import { AdornedInput, Adornment, FormField, FormHelp, Heading, Label, Listbox, Textarea } from '@zillow/constellation';
import { CurrencyInput } from '@zillow/zrs-comet';

import type { Section } from '../../../../../models';
import { CURRENCY_INPUT_PLACEHOLDER } from '../../../../../constants/stringConstants';
import { OTHER_METHOD_VALUE } from '../../../../../constants';

type Props = {
  section: Section;
  administrativeFeeField?: string;
  listBoxField: string;
  otherMethodDescriptionField: string;
  otherMethodField: string;
  setSectionValues: (keyValuePairs: object) => void;
  options: Array<{ value: string; label: string }>;
  utilityName: string;
};

export default function UtilitiesCalculation({
  section,
  setSectionValues,
  administrativeFeeField,
  listBoxField,
  options,
  otherMethodDescriptionField,
  utilityName,
  otherMethodField,
}: Props): JSX.Element {
  function onAdministrativeFeeChange(event: any): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [administrativeFeeField]: value,
    });
  }

  function onOptionChange(value: string): void {
    if (value === OTHER_METHOD_VALUE) {
      setSectionValues({
        [listBoxField]: value,
        [otherMethodField]: true,
      });
    } else {
      setSectionValues({
        [listBoxField]: value,
        [otherMethodDescriptionField]: null,
        [otherMethodField]: false,
      });
    }
  }

  function onOtherMethodDescriptionChange(event: FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [otherMethodDescriptionField]: value,
    });
  }

  return (
    <>
      <Heading level={6} marginBottom="sm">
        {utilityName}
      </Heading>
      {administrativeFeeField && section.getField(administrativeFeeField) && (
        <FormField
          marginBottom="sm"
          label={<Label optional>What is the administrative fee?</Label>}
          control={
            <AdornedInput
              leftAdornment={<Adornment>$</Adornment>}
              rightAdornment={<Adornment>per month</Adornment>}
              input={
                <CurrencyInput
                  placeholder={CURRENCY_INPUT_PLACEHOLDER}
                  value={section.getFieldValue(administrativeFeeField)}
                  onChange={onAdministrativeFeeChange}
                  onBlur={onAdministrativeFeeChange}
                />
              }
            />
          }
          description={
            <FormHelp>
              This should reflect actual administrative costs that you have incurred in your use of a ratio billing
              system.
            </FormHelp>
          }
        />
      )}
      <FormField
        label={<Label>How did you calculate the monthly amount for {utilityName.toLowerCase()}?</Label>}
        control={
          <Listbox
            value={section.getFieldValue(listBoxField)}
            appearance="radio"
            onChange={onOptionChange}
            options={options}
          />
        }
      />
      {section.getFieldValue(otherMethodField) && (
        <FormField
          marginTop="sm"
          label={<Label>Describe this other method</Label>}
          control={
            <Textarea
              resize="vertical"
              value={section.getFieldValue(otherMethodDescriptionField)}
              onChange={onOtherMethodDescriptionChange}
            />
          }
        />
      )}
    </>
  );
}
